import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { DropArrowIcon } from './icons'

interface ChartDatum {
	month: string
	profit: number
	volume: number
}

interface PerformanceChartProps {
	title: string
	data: ChartDatum[]
	profitColor: string
	volumeColor: string
	profitGradientId: string
	volumeGradientId: string
}

export function PerformanceChart({ title, data, profitColor, volumeColor, profitGradientId, volumeGradientId }: PerformanceChartProps) {
	return (
		<div className='flex min-w-0 flex-1 flex-col gap-3 rounded-[14px] border border-border bg-card p-1 pt-3 shadow-[0_4px_12px_0_hsl(var(--background)/0.2)]'>
			<div className='flex flex-col gap-3 px-3 sm:flex-row sm:items-center sm:gap-6'>
				<h3 className='min-w-0 flex-1 text-base font-medium tracking-[-0.24px] text-foreground sm:text-lg sm:tracking-[-0.27px]'>{title}</h3>
				<button
					type='button'
					className='flex items-center gap-2 rounded-xl border border-border bg-secondary px-4 py-2 text-sm font-medium tracking-[-0.21px] text-foreground'
				>
					2024
					<DropArrowIcon className='h-4 w-4 text-foreground' />
				</button>
			</div>

			<div className='h-[220px] rounded-xl bg-background pb-3 pt-3 sm:h-[260px]'>
				<ResponsiveContainer width='100%' height='100%'>
					<AreaChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
						<defs>
							<linearGradient id={profitGradientId} x1='0' y1='0' x2='0' y2='1'>
								<stop offset='0%' stopColor={profitColor} stopOpacity={0.35} />
								<stop offset='100%' stopColor={profitColor} stopOpacity={0} />
							</linearGradient>
							<linearGradient id={volumeGradientId} x1='0' y1='0' x2='0' y2='1'>
								<stop offset='0%' stopColor={volumeColor} stopOpacity={0.35} />
								<stop offset='100%' stopColor={volumeColor} stopOpacity={0} />
							</linearGradient>
						</defs>
						<CartesianGrid stroke='hsl(var(--border))' strokeOpacity={0.5} vertical={false} />
						<XAxis dataKey='month' tick={{ fill: 'hsl(var(--subtle-foreground))', fontSize: 12 }} axisLine={false} tickLine={false} />
						<YAxis
							tick={{ fill: 'hsl(var(--subtle-foreground))', fontSize: 12 }}
							axisLine={false}
							tickLine={false}
							tickFormatter={(v: number) => v.toLocaleString()}
							width={64}
						/>
						<Tooltip
							contentStyle={{
								background: 'hsl(var(--secondary))',
								border: '1px solid hsl(var(--border))',
								borderRadius: 10,
								color: 'hsl(var(--foreground))',
							}}
						/>
						<Area type='monotone' dataKey='profit' stroke={profitColor} strokeWidth={1.5} fill={`url(#${profitGradientId})`} />
						<Area type='monotone' dataKey='volume' stroke={volumeColor} strokeWidth={1.5} fill={`url(#${volumeGradientId})`} />
					</AreaChart>
				</ResponsiveContainer>
			</div>

			<div className='flex items-center justify-center gap-6 pb-2'>
				<span className='flex items-center gap-2 text-base font-medium tracking-[-0.24px] text-muted-foreground'>
					<span className='h-2 w-2 rounded-full' style={{ backgroundColor: profitColor }} />
					Profit
				</span>
				<span className='flex items-center gap-2 text-base font-medium tracking-[-0.24px] text-muted-foreground'>
					<span className='h-2 w-2 rounded-full' style={{ backgroundColor: volumeColor }} />
					Volume
				</span>
			</div>
		</div>
	)
}
